import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  MeetingRoom as MeetingRoomIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  EventAvailable as EventAvailableIcon
} from '@mui/icons-material';
import { listarMinhasReservas, cancelarReserva, concluirReserva, Reserva } from '../../services/reservaService';
import { format } from 'date-fns';
import { alpha } from '@mui/material/styles';

// Função para interpretar data do banco como horário local (sem conversão UTC)
const toBrasiliaTime = (date: Date | string): Date => {
  if (typeof date === 'string') {
    // Remover 'Z' ou timezone da string para forçar interpretação como local
    const dateStr = date.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
    return new Date(dateStr);
  }
  return date;
};

const MinhasReservas: React.FC = () => {
  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [tabAtiva, setTabAtiva] = useState(0);
  const [dialogCancelar, setDialogCancelar] = useState(false);
  const [reservaSelecionada, setReservaSelecionada] = useState<Reserva | null>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [processando, setProcessando] = useState(false);

  useEffect(() => {
    carregarReservas();
  }, [tabAtiva]);

  const carregarReservas = async () => {
    try {
      setCarregando(true);
      setErro('');
      
      let status: 'ativa' | 'cancelada' | 'concluida' | undefined;
      if (tabAtiva === 0) status = 'ativa';
      else if (tabAtiva === 1) status = 'concluida';
      else if (tabAtiva === 2) status = 'cancelada';
      
      const dados = await listarMinhasReservas(status);
      setReservas(dados);
    } catch (error: any) {
      console.error('Erro ao carregar reservas:', error);
      setErro(error.response?.data?.error || 'Erro ao carregar reservas');
    } finally {
      setCarregando(false);
    }
  };

  const handleAbrirDialogCancelar = (reserva: Reserva) => {
    setReservaSelecionada(reserva);
    setMotivoCancelamento('');
    setDialogCancelar(true);
  };

  const handleFecharDialogCancelar = () => {
    setDialogCancelar(false);
    setReservaSelecionada(null);
    setMotivoCancelamento('');
  };

  const handleCancelarReserva = async () => {
    if (!reservaSelecionada) return;
    
    try {
      setProcessando(true);
      await cancelarReserva(reservaSelecionada.id, motivoCancelamento);
      handleFecharDialogCancelar();
      carregarReservas();
    } catch (error: any) {
      console.error('Erro ao cancelar reserva:', error);
      setErro(error.response?.data?.error || 'Erro ao cancelar reserva');
    } finally {
      setProcessando(false);
    }
  };

  const handleConcluirReserva = async (reservaId: string) => {
    try {
      setProcessando(true);
      await concluirReserva(reservaId);
      carregarReservas();
    } catch (error: any) {
      console.error('Erro ao concluir reserva:', error);
      setErro(error.response?.data?.error || 'Erro ao concluir reserva');
    } finally {
      setProcessando(false);
    }
  };

  const getStatusChip = (status: string) => {
    // Cores semânticas do tema (as mesmas usadas em todo o resto do
    // sistema) em vez das cores "flat design" fixas de antes — essas se
    // adaptam automaticamente ao modo escuro, as antigas não.
    const configs = {
      ativa: { label: 'Ativa', cor: 'success.main' as const },
      concluida: { label: 'Concluída', cor: 'info.main' as const },
      cancelada: { label: 'Cancelada', cor: 'error.main' as const },
    };

    const config = configs[status as keyof typeof configs] || configs.ativa;

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: (theme) => alpha(theme.palette[config.cor.split('.')[0] as 'success' | 'info' | 'error'].main, 0.15),
          color: config.cor,
          fontWeight: 'bold',
        }}
      />
    );
  };

  const renderReserva = (reserva: Reserva) => {
    const dataInicio = toBrasiliaTime(reserva.data_inicio);
    const dataFim = toBrasiliaTime(reserva.data_fim);
    const isPassada = dataFim < new Date();
    const isFutura = dataInicio > new Date();
    
    return (
      <Card key={reserva.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MeetingRoomIcon sx={{ color: reserva.sala_cor }} />
                <Typography variant="h6">
                  {reserva.titulo}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {reserva.sala_nome}
              </Typography>
              
              {getStatusChip(reserva.status)}
            </Box>
          </Box>

          {reserva.descricao && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {reserva.descricao}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccessTimeIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {format(dataInicio, "dd/MM/yyyy 'às' HH:mm")}
              {' até '}
              {format(dataFim, "dd/MM/yyyy 'às' HH:mm")}
            </Typography>
          </Box>

          {reserva.observacoes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              Obs: {reserva.observacoes}
            </Typography>
          )}

          {reserva.status === 'ativa' && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleConcluirReserva(reserva.id)}
                disabled={processando}
                color="success"
              >
                Concluir
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => handleAbrirDialogCancelar(reserva)}
                disabled={processando}
              >
                Cancelar
              </Button>
            </Box>
          )}

          {reserva.status === 'cancelada' && reserva.motivo_cancelamento && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Motivo do cancelamento: {reserva.motivo_cancelamento}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  if (carregando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Minhas Reservas
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gerencie suas reservas de salas
        </Typography>
      </Box>

      {erro && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabAtiva} onChange={(_, newValue) => setTabAtiva(newValue)}>
          <Tab label="Ativas" icon={<EventAvailableIcon />} iconPosition="start" />
          <Tab label="Concluídas" icon={<CheckCircleIcon />} iconPosition="start" />
          <Tab label="Canceladas" icon={<CancelIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {reservas.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Nenhuma reserva encontrada
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {reservas.map(renderReserva)}
        </Stack>
      )}

      {/* Dialog de cancelamento */}
      <Dialog open={dialogCancelar} onClose={handleFecharDialogCancelar} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar Reserva</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tem certeza que deseja cancelar esta reserva?
          </Typography>
          
          {reservaSelecionada && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{reservaSelecionada.titulo}</strong>
              <br />
              {reservaSelecionada.sala_nome}
              <br />
              {format(toBrasiliaTime(reservaSelecionada.data_inicio), "dd/MM/yyyy 'às' HH:mm")}
            </Alert>
          )}

          <TextField
            label="Motivo do cancelamento (opcional)"
            fullWidth
            multiline
            rows={3}
            value={motivoCancelamento}
            onChange={(e) => setMotivoCancelamento(e.target.value)}
            placeholder="Informe o motivo do cancelamento"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharDialogCancelar} disabled={processando}>
            Voltar
          </Button>
          <Button
            onClick={handleCancelarReserva}
            color="error"
            variant="contained"
            disabled={processando}
            startIcon={processando ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            {processando ? 'Cancelando...' : 'Confirmar Cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MinhasReservas;
